import {
    generateMonthListBetweenDates,
    getFirstDayOfMonth,
    getFirstDayOfSpecifiedMonth,
    getLastDayOfSpecifiedMonth,
  } from '../../utils/date';
import { localization } from './localization';
import { Route, Switch } from 'react-router-dom';
import { sortByProperty } from '../../utils/array';
import DailyUsage from './components/Daily/DailyUsage';
import { useTenant } from '../../contexts/TenantContext';
import { useCommon } from '../../contexts/CommonContext';
import MonthlyUsage from './components/Monthly/MonthlyUsage';
import { BdsSelectOption } from 'blip-ds/dist/blip-ds-react';
import { startLoading, stopLoading } from '../../api/CommonApi';
import { useContentLocalizer } from '../../hooks/useContentLocalizer';
import { billingRoutes, billingSubRoutes } from '../../routes/BillingRoutes';
import DailyEngagedUsersSummary from './components/DailyEngagedUsersSummary';
import DailyEngagedUsersContext from '../../contexts/DailyEngagedUsersContext';
import React, { Reducer, useEffect, useMemo, useReducer, useState } from 'react';
import BillingDailyActiveUsersServiceApi from '../../api/BillingDailyActiveUsersApi';
import  { DailyEngagedUserUsage } from '../../models/dailyEngagedUsers/DailyEngagedUserUsage';
import { DailyEngagedUsersMonthlyUsage } from '../../models/dailyEngagedUsers/DailyEngagedUsersMonthlyUsage';
import { CompiledDailyEngagedUserConsumption } from '../../models/dailyEngagedUsers/CompiledDailyEngagedUserConsumption';

interface CompiledDailyEngagedUserDataState {
  compiledDailyEngagedUserConsumption: CompiledDailyEngagedUserConsumption;
  compiledContainsDataOnSelectedMonth: boolean;
  compiledMonthDate: string;
  compiledContainsOverspent: boolean;
  showPlanInformations: boolean;
}

const DailyEngagedUsers: React.FC = () => {
    const {
        setHeaderContent,
        common: { config, loggedUser },
      } = useCommon();
    
      const { tenant } = useTenant();
      const content = useContentLocalizer(localization);
      const billingDailyEngagedUserApi = new BillingDailyActiveUsersServiceApi();
      const initialDate = new Date(config.REVENUE_START_DATE);
      initialDate.setSeconds(0);
      const [dateToSearch, setDateToSearch] = useState(getFirstDayOfMonth());
      const [dailyEngagedUserMonthlyUsage, setDailyEngagedUserMonthlyUsage] = useState<DailyEngagedUsersMonthlyUsage[]>([]);
      const [dailyEngagedUserUsage, setDailyEngagedUserUsage] = useState<DailyEngagedUserUsage[]>([]);
    
      const [compiledDailyEngagedUserDataState, setMonthlyActiveMessageUsageState] = useReducer<
        Reducer<CompiledDailyEngagedUserDataState, Partial<CompiledDailyEngagedUserDataState>>
      >(
        (compiledDailyEngagedUserDataState, newCompiledDailyEngagedUserDataState) => ({
          ...compiledDailyEngagedUserDataState,
          ...newCompiledDailyEngagedUserDataState,
        }),
        {
          compiledDailyEngagedUserConsumption: {
            metric: null,
            total: 0,
            planUsageRate: 0,
            exceededPlan: 0,
            planUsage: 0,
            startDateReference: getFirstDayOfSpecifiedMonth(new Date()),
            endDateReference: getLastDayOfSpecifiedMonth(new Date()),
            availablePlan: 0, 
          },
          compiledContainsDataOnSelectedMonth: false,
          compiledContainsOverspent: false,
          compiledMonthDate: '',
          showPlanInformations: false,
        },
      );
    
      useEffect(() => {
        startLoading();
    
        setHeaderContent({
          redirect: '/',
          text: content.formatString(content.header, tenant.info.name),
        });
        stopLoading();
    
        return () => setHeaderContent({ redirect: null });
      }, []);
    
      const periodOptions = useMemo(() => {
        const monthList = generateMonthListBetweenDates(initialDate, new Date()).sort(
          (first, second) => second.getTime() - first.getTime(),
        );
    
        return monthList.map((monthDate) => {
          const month = monthDate.getMonth();
          const year = monthDate.getFullYear();
    
          return (
            <BdsSelectOption value={monthDate.toISOString()} key={`${month}-${year}`}>
              {content.compiledDailyEngagedUserInformation.month[month]} {year}
            </BdsSelectOption>
          );
        });
      }, []);
    
      const sortDailyEngagedUserMonthlyUsage = async (sort) => {
        const sortedChatbotsConsumption = sortByProperty(dailyEngagedUserMonthlyUsage, sort.property, sort.order);
        setDailyEngagedUserMonthlyUsage([...sortedChatbotsConsumption]);
      };
    
      const sortDailyEngagedUserUsage = async (sort) => {
        const sortedChatbotsConsumption = sortByProperty(dailyEngagedUserUsage, sort.property, sort.order);
        setDailyEngagedUserUsage([...sortedChatbotsConsumption]);
      };
    
      const currentMonth = useMemo(() => {
        const month = dateToSearch.getMonth() + 1 === new Date().getMonth() + 1;
    
        const year = dateToSearch.getFullYear() === new Date().getFullYear();
    
        return month && year;
      }, [dateToSearch]);
    
      const contextProps = useMemo(() => ({
        currentMonth,
        dateToSearch,
        periodOptions,
        content,
        tenant,
        loggedUser,
        billingDailyEngagedUserApi,
        dailyEngagedUserMonthlyUsage,
        dailyEngagedUserUsage,
        setDateToSearch,
        sortDailyEngagedUserMonthlyUsage,
        setDailyEngagedUserMonthlyUsage,
        sortDailyEngagedUserUsage,
        setDailyEngagedUserUsage,
        compiledDailyEngagedUserDataState,
        setMonthlyActiveMessageUsageState,
      }), [
        currentMonth,
        dateToSearch,
        periodOptions,
        content,
        tenant,
        loggedUser,
        billingDailyEngagedUserApi,
        dailyEngagedUserMonthlyUsage,
        dailyEngagedUserUsage,
        setDateToSearch,
        sortDailyEngagedUserMonthlyUsage,
        setDailyEngagedUserMonthlyUsage,
        sortDailyEngagedUserUsage,
        setDailyEngagedUserUsage,
        compiledDailyEngagedUserDataState,
        setMonthlyActiveMessageUsageState,
      ]);

        return (
        <DailyEngagedUsersContext.Provider value={contextProps}>
        <Switch>
            <Route exact path={billingRoutes.dailyEngagedUsers} component={DailyEngagedUsersSummary}></Route>
            <Route exact path={billingSubRoutes.dailyEngagedUsersDaily} component={MonthlyUsage}></Route>
            <Route exact path={billingSubRoutes.dailyEngagedUsersMonthly} component={DailyUsage}></Route>
        </Switch>
        </DailyEngagedUsersContext.Provider>
        );
};

export default DailyEngagedUsers;